import React, { useState, useEffect } from 'react';
import { FaTimes, FaUniversity, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useStripe, useElements, IbanElement } from '@stripe/react-stripe-js';
import { modalVariants } from '../animations';
import api from '../../../services/api';

const EditIbanModal = ({ isOpen, onClose, currentUser, onSave }) => {
  const [showIban, setShowIban] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [last4, setLast4] = useState('');
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem('token');
    api.get('/users/payment-method', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLast4(r.data?.last4 || ''))
      .catch(() => setLast4(''));
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe n\'est pas encore chargé');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const authToken = localStorage.getItem("token");
      
      // Créer un SetupIntent pour l'IBAN
      const clientSecretResponse = await api.post(
        "/subscriptions/intent",
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const clientSecret = clientSecretResponse.data.client_secret;
      const ibanElement = elements.getElement(IbanElement);

      if (!ibanElement) {
        setError('Élément IBAN non disponible');
        return;
      }

      // Confirmer le setup SEPA avec Stripe
      const { error, setupIntent } = await stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: {
            name: `${currentUser.first_name} ${currentUser.last_name}`,
            email: currentUser.email,
          },
        },
        return_url: `${window.location.origin}/dashboard`,
      });

      if (error) {
        setError(error.message);
        return;
      }

      if (setupIntent.status === "requires_action" && setupIntent.next_action) {
        const redirectUrl = setupIntent.next_action.redirect_to_url.url;
        window.location.href = redirectUrl;
        return;
      }

      const paymentMethodId = setupIntent.payment_method;

      // Mettre à jour le moyen de paiement via l'API
      await api.post(
        "/users/update-payment-method",
        {
          iban: paymentMethodId,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // Appeler le callback de sauvegarde
      if (onSave) {
        await onSave(paymentMethodId);
      }

      onClose();
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'IBAN:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde de l\'IBAN');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setShowIban(false);
    onClose();
  };

  const ibanElementOptions = {
    supportedCountries: ['SEPA'],
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div {...modalVariants.overlay} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div {...modalVariants.content} className="bg-white rounded-2xl w-full max-w-lg max-h-[95vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-fideliz-blue/10 rounded-lg flex items-center justify-center">
                  <FaUniversity className="w-5 h-5 text-fideliz-blue" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Modifier mon IBAN</h2>
                  <p className="text-sm text-gray-500 mt-1">Pour recevoir vos paiements</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8">
              <div className="space-y-6">
                {/* IBAN actuel */}
                {(currentUser?.iban || last4) && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      IBAN actuel
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 font-mono flex-1">
                        {showIban && currentUser?.iban
                          ? currentUser.iban
                          : last4
                            ? `•••• •••• •••• •••• •••• ${last4}`
                            : '•••• •••• •••• •••• •••• ••••'}
                      </span>
                      {currentUser?.iban && (
                        <button
                          type="button"
                          onClick={() => setShowIban(!showIban)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                        >
                          {showIban ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Nouvel IBAN avec Stripe Elements */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Nouvel IBAN
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 focus-within:ring-2 focus-within:ring-fideliz-blue focus-within:border-transparent">
                    <IbanElement options={ibanElementOptions} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Saisie sécurisée via Stripe - Format SEPA uniquement
                  </p>
                </div>

                {/* Message d'erreur */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {/* Informations importantes */}
                <div className="bg-fideliz-blue/5 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-fideliz-blue mb-2">Informations importantes</h4>
                  <ul className="text-xs text-fideliz-blue/80 space-y-1">
                    <li>• L'IBAN doit être valide et actif</li>
                    <li>• Le prélèvement SEPA se fera sur ce compte</li>
                    <li>• Vérifiez bien les informations avant de confirmer</li>
                  </ul>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-fideliz-blue text-white rounded-lg hover:bg-fideliz-blue hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <FaCheck className="w-4 h-4" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditIbanModal;
